import React, { lazy, Suspense, useMemo, useState } from 'react'
import PinIcon from '@mui/icons-material/PlaceOutlined'
import SearchIcon from '@mui/icons-material/Search'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { buscar, comDistancia, formatarDistancia, formatarRaio, Loja, LojaComDistancia, noRaio, PRECISAO_FRACA_M, raiosOferecidos } from './geo'
import { lojasRecentes } from './recentes'
import { useLocalizacao } from './useLocalizacao'
import './loja.scss'

// O mapa (e o Leaflet com ele) só é baixado quando aparece.
const MapaDeLojas = lazy(() => import('./MapaDeLojas'))

interface Props {
  lojas: Loja[]
  raioInicialM: number
  /** Sem o botão "Ver todas as lojas": só abre o relatório quem escolhe uma loja. */
  obrigatorio: boolean
  /** De onde vêm as lojas recentes (dashboard e ambiente). */
  chaveRecentes: string
  aoEscolher: (loja: Loja) => void
  aoVerTodas?: () => void
  /** Frase no topo, por exemplo "A loja 123 não está na sua lista". */
  aviso?: string | null
  /** A lista não carregou: só mostra o erro e, se puder, o caminho sem escolher. */
  erro?: string | null
}

// Só as mais perto aparecem sem busca: com milhares de lojas num raio grande, a lista inteira pesa e ninguém lê.
const MAXIMO_NA_LISTA = 50

const enderecoDa = (loja: Loja) => [loja.endereco, loja.cidade && loja.uf ? `${loja.cidade}/${loja.uf}` : loja.cidade || loja.uf].filter(Boolean).join(' · ')

function Linha({ loja, aoMarcar }: { loja: LojaComDistancia; aoMarcar: (l: LojaComDistancia) => void }) {
  const local = enderecoDa(loja)
  return (
    <li>
      <button type="button" className="sl-item" onClick={() => aoMarcar(loja)}>
        <span className="sl-item-texto">
          <b>{loja.nome}</b>
          <span>{local || (loja.bandeira ? loja.bandeira : `Código ${loja.id}`)}</span>
        </span>
        {loja.km != null && <span className="sl-dist">{formatarDistancia(loja.km)}</span>}
        <ChevronRightIcon fontSize="small" className="sl-seta" />
      </button>
    </li>
  )
}

/**
 * Escolher a loja onde a pessoa está, antes de abrir o relatório. Pede a localização (só depois de explicar para que serve), ordena
 * as lojas por distância e deixa buscar pelo nome quando a localização não ajuda. A conta de distância é toda feita aqui:
 * a posição não vai ao servidor.
 */
export default function SeletorDeLoja({ lojas, raioInicialM, obrigatorio, chaveRecentes, aoEscolher, aoVerTodas, aviso, erro }: Props) {
  const { situacao, posicao, pedir } = useLocalizacao()
  const [raio, setRaio] = useState(raioInicialM)
  const [termo, setTermo] = useState('')
  // Quem prefere não compartilhar a posição vai direto à busca, sem a tela de explicação.
  const [pulou, setPulou] = useState(false)
  // A loja tocada (na lista ou no mapa) pede confirmação: um pino toca fácil na vizinha, e abrir o relatório da loja errada engana.
  const [escolhida, setEscolhida] = useState<LojaComDistancia | null>(null)
  const recentes = useMemo(() => lojasRecentes(chaveRecentes), [chaveRecentes])

  const todas = useMemo(() => comDistancia(lojas, posicao), [lojas, posicao])
  const recentesNaLista = useMemo(
    () => recentes.map(id => todas.find(l => l.id === id)).filter((l): l is LojaComDistancia => !!l),
    [recentes, todas]
  )

  const buscando = termo.trim().length > 0
  const achadas = useMemo(() => (buscando ? buscar(todas, termo) : []), [buscando, todas, termo])
  const pertoDeMim = useMemo(() => noRaio(todas, raio), [todas, raio])
  const comPosicao = situacao === 'ok' && posicao != null
  const semCoordenada = useMemo(() => todas.filter(l => l.km == null).length, [todas])
  const raios = useMemo(() => raiosOferecidos(raioInicialM), [raioInicialM])
  const proximoRaio = raios.find(r => r > raio)
  // A loja marcada aparece no mapa mesmo quando veio da busca e está fora do raio.
  const noMapa = useMemo(
    () => (escolhida && !pertoDeMim.some(l => l.id === escolhida.id) ? [...pertoDeMim, escolhida] : pertoDeMim),
    [pertoDeMim, escolhida]
  )

  if (erro) {
    return (
      <div className="sl" role="alert">
        <div className="sl-centro">
          <h2>Não deu para carregar as lojas</h2>
          <p>{erro}</p>
          {!obrigatorio && aoVerTodas && <button type="button" className="sl-botao sec" onClick={aoVerTodas}>Abrir sem escolher a loja</button>}
        </div>
      </div>
    )
  }

  if (lojas.length === 0) {
    return (
      <div className="sl">
        <div className="sl-centro">
          <h2>Nenhuma loja disponível para você</h2>
          <p>Este relatório não encontrou lojas dentro do que você pode ver. Fale com o administrador do BI.</p>
          {!obrigatorio && aoVerTodas && <button type="button" className="sl-botao sec" onClick={aoVerTodas}>Abrir mesmo assim</button>}
        </div>
      </div>
    )
  }

  const explicando = situacao === 'inicial' && !pulou
  const mapaDaTela = comPosicao ? (
    <Suspense fallback={<div className="sl-mapa carregando">Carregando o mapa…</div>}>
      <MapaDeLojas posicao={posicao!} raioM={raio} lojas={noMapa} escolhida={escolhida?.id} aoTocar={setEscolhida} />
    </Suspense>
  ) : null
  const rodape = !obrigatorio && aoVerTodas
    ? <button type="button" className="sl-link" onClick={aoVerTodas}>Ver todas as lojas, sem escolher uma</button>
    : null

  return (
    <div className="sl">
      <div className="sl-corpo">
        {aviso && <p className="sl-aviso" role="status">{aviso}</p>}

        {escolhida ? (
          <>
            {mapaDaTela}
            <div className="sl-confirma">
              <h2>{escolhida.nome}</h2>
              {enderecoDa(escolhida) && <p>{enderecoDa(escolhida)}</p>}
              <dl>
                {escolhida.km != null && <div><dt>Distância</dt><dd>{formatarDistancia(escolhida.km)}</dd></div>}
                {/* Quando o cadastro tem um campo só para a loja, o código é o próprio nome: repetir não ajuda. */}
                {escolhida.id !== escolhida.nome && <div><dt>Código</dt><dd>{escolhida.id}</dd></div>}
                {escolhida.bandeira && <div><dt>Bandeira</dt><dd>{escolhida.bandeira}</dd></div>}
              </dl>
              <button type="button" className="sl-botao" onClick={() => aoEscolher(escolhida)}>Abrir relatório desta loja</button>
              <button type="button" className="sl-botao link" onClick={() => setEscolhida(null)}>Escolher outra loja</button>
            </div>
          </>
        ) : explicando ? (
          <div className="sl-centro">
            <span className="sl-selo"><PinIcon /></span>
            <h2>Veja as lojas perto de você</h2>
            <p>O portal usa sua localização só para ordenar as lojas por distância. Ela fica no seu aparelho e não é enviada nem gravada.</p>
            <button type="button" className="sl-botao" onClick={pedir}>Usar minha localização</button>
            <button type="button" className="sl-botao link" onClick={() => setPulou(true)}>Prefiro buscar pelo nome</button>
          </div>
        ) : (
          <>
            {situacao === 'pedindo' && <p className="sl-aviso neutro" role="status">Buscando sua posição…</p>}
            {situacao === 'negada' && (
              <p className="sl-aviso" role="status">
                A localização está bloqueada neste navegador. Para ver as lojas por distância, libere a localização para este site nas configurações do navegador e abra de novo. Enquanto isso, busque a loja pelo nome.
              </p>
            )}
            {(situacao === 'indisponivel' || situacao === 'erro') && (
              <p className="sl-aviso" role="status">
                Não consegui sua posição agora. <button type="button" className="sl-link" onClick={pedir}>Tentar de novo</button> ou busque a loja pelo nome.
              </p>
            )}
            {comPosicao && posicao!.precisao > PRECISAO_FRACA_M && (
              <p className="sl-aviso" role="status">
                Precisão de cerca de {Math.round(posicao!.precisao / 10) * 10} m: as distâncias podem não bater. <button type="button" className="sl-link" onClick={pedir}>Tentar de novo</button>
              </p>
            )}

            <label className="sl-busca">
              <SearchIcon fontSize="small" />
              <input
                value={termo}
                onChange={e => setTermo(e.target.value)}
                placeholder="Buscar por nome, código ou cidade"
                aria-label="Buscar loja por nome, código ou cidade"
                autoFocus={pulou || situacao === 'negada' || situacao === 'indisponivel'}
              />
            </label>

            {buscando ? (
              <>
                <div className="sl-cab"><b>{achadas.length} {achadas.length === 1 ? 'loja' : 'lojas'}</b><small>{comPosicao ? 'mais perto primeiro' : 'em ordem alfabética'}</small></div>
                {achadas.length === 0
                  ? <p className="sl-vazio">Nenhuma loja com esse nome. Confira a grafia ou procure pela cidade.</p>
                  : <ul className="sl-lista">{achadas.slice(0, MAXIMO_NA_LISTA).map(l => <Linha key={l.id} loja={l} aoMarcar={setEscolhida} />)}</ul>}
              </>
            ) : comPosicao ? (
              <>
                <div className="sl-raios" role="group" aria-label="Raio de busca">
                  {raios.map(r => (
                    <button key={r} type="button" className="sl-chip" aria-pressed={r === raio} onClick={() => setRaio(r)}>{formatarRaio(r)}</button>
                  ))}
                  <button type="button" className="sl-chip icone" onClick={pedir} aria-label="Atualizar minha posição" title="Atualizar minha posição"><MyLocationIcon fontSize="inherit" /></button>
                </div>
                {mapaDaTela}
                <div className="sl-cab">
                  <b>{pertoDeMim.length} {pertoDeMim.length === 1 ? 'loja' : 'lojas'} no raio</b><small>mais perto primeiro</small>
                </div>
                {pertoDeMim.length === 0 ? (
                  <div className="sl-vazio">
                    <b>Nenhuma loja a menos de {formatarRaio(raio)}</b>
                    <span>Amplie o raio ou busque a loja pelo nome.</span>
                    {proximoRaio && <button type="button" className="sl-botao sec" onClick={() => setRaio(proximoRaio)}>Ampliar para {formatarRaio(proximoRaio)}</button>}
                  </div>
                ) : (
                  <>
                    <ul className="sl-lista">{pertoDeMim.slice(0, MAXIMO_NA_LISTA).map(l => <Linha key={l.id} loja={l} aoMarcar={setEscolhida} />)}</ul>
                    {pertoDeMim.length > MAXIMO_NA_LISTA && <p className="sl-nota">Mostrando as {MAXIMO_NA_LISTA} mais perto. Reduza o raio ou busque pelo nome.</p>}
                  </>
                )}
                {semCoordenada > 0 && <p className="sl-nota">{semCoordenada} {semCoordenada === 1 ? 'loja não tem' : 'lojas não têm'} localização cadastrada e só aparece na busca.</p>}
              </>
            ) : recentesNaLista.length > 0 ? (
              <>
                <div className="sl-cab"><b>Lojas recentes</b></div>
                <ul className="sl-lista">{recentesNaLista.map(l => <Linha key={l.id} loja={l} aoMarcar={setEscolhida} />)}</ul>
              </>
            ) : null}
          </>
        )}
      </div>
      {rodape && <div className="sl-rodape">{rodape}</div>}
    </div>
  )
}
